import React, { useCallback, useState } from 'react';

import { ID } from '../../../../../types';
import { IconsEnum } from '../../../../../assets/icons/types';

import { CREATE_COMPANY_TAB_QUERY } from '../../../queries/createCompanyTab.query';

import { useCreateCompanyTab } from '../../../hooks/useCreateCompanyTab';

import { ProfileInputEditableField } from '../../../../teams/helpers/ProfileInputEditableField';
import { ButtonHelper } from '../../../../../helpers/buttons/ButtonHelper';
import { Icon } from '../../../../../helpers/Icon';

import { CompanyTabsCache } from '../../../CompanyTabsCache';

interface CompanyTabsMenuDropdownCreateItemProps {
  companyId: ID;
}

function CompanyTabsMenuDropdownCreateItem({
  companyId
}: CompanyTabsMenuDropdownCreateItemProps) {
  const [isCreating, setIsCreating] = useState<boolean>(false);

  const {
    createCompanyTab,
    createCompanyTabLoading,
    createCompanyTabErrorMessage,
    createCompanyTabReset
  } = useCreateCompanyTab({
    query: CREATE_COMPANY_TAB_QUERY,
    cacheKeys: [CompanyTabsCache.indexCacheKey()]
  });

  const handleCreateTab = useCallback<(name: string) => Promise<unknown>>(
    (name) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        setIsCreating(false);
        return Promise.resolve();
      }
      return createCompanyTab({
        companyId,
        name: trimmedName
      });
    },
    [createCompanyTab, companyId]
  );

  return (
    <li className="bg-gray-500 duration-150 ease-in-out flex group items-center py-2 relative transition-colors -mx-4 pl-6 pr-4 bg-opacity-0 hover:bg-opacity-20">
      {isCreating ? (
        <ProfileInputEditableField
          className="w-full"
          inputClassName="number-appearance-none leading-none border-0 text-sm py-1 px-2 w-full rounded placeholder:italic bg-transparent dark:placeholder:text-gray-600 hover:bg-gray-200 dark:focus:bg-gray-800 dark:hover:bg-gray-800"
          fieldName="createCompanyTab"
          defaultValue=""
          onSubmit={handleCreateTab}
          showEditIcon={false}
          disabled={createCompanyTabLoading}
          onResetEdit={() => setIsCreating(false)}
          errorMessage={createCompanyTabErrorMessage}
          autoFocus
        />
      ) : (
        <ButtonHelper
          disabled={createCompanyTabLoading}
          onClick={() => {
            createCompanyTabReset();
            setIsCreating(true);
          }}
          className="grow flex items-center justify-center"
        >
          <span className="p-1 rounded-full text-gray-500 transition-colors group-hover:text-gray-950 dark:group-hover:text-white group-hover:bg-gray-200 dark:group-hover:bg-gray-700">
            <Icon icon={IconsEnum.PLUS} className="h-5 w-5" />
          </span>
        </ButtonHelper>
      )}
    </li>
  );
}

export default CompanyTabsMenuDropdownCreateItem;
